import { Inject, Injectable, Optional } from '@angular/core';
import { from, Observable, zip } from 'rxjs';
import { GraphQLService } from './graph-ql.service';
import { map } from 'rxjs/operators';
import {
  ActivateCustomerData,
  CreateAccessTokenData,
  CreateCustomerData,
  Customer,
  CustomerAccessToken,
  ShopifyError,
  ResetCustomerData,
} from '../models';
import { mailingAddress } from './addresses.service';
import { HttpClient } from '@angular/common/http';
import { BASE_URL } from '@teamfoster/sdk/ssr';

@Injectable({
  providedIn: 'root',
})
export class CustomerService extends GraphQLService {
  private STORAGE_KEY = 'CUSTOMER_TOKEN';

  constructor(
    private http: HttpClient,
    @Optional()
    @Inject(BASE_URL)
    private BASE_URL: string
  ) {
    super();
  }

  GetCustomer(token: string, language?: string) {
    const query = this.client.query(`@inContext(language: ${language})`, (root: any) => {
      root.add('shop', (shop: any) => {
        shop.add('name');
      });
      root.add('customer', { args: { customerAccessToken: token } }, (pages: any) => {
        customer(pages, this.client);
      });
    });
    return from(this.client.send(query)).pipe(
      map((a: any) => {
        const errors = a['errors'] || [];
        if (errors.length) {
          throw errors as ShopifyError[];
        }

        return a['data'].customer;
      })
    ) as Observable<Customer>;
    // return from(this.client.send(query)).pipe(map(({ data, model }) => data.pageByHandle));
  }

  CreateCustomer(user: CreateCustomerData, language?: string) {
    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerCreate', { args: { input: user } }, (customerCreate: any) => {
        customerCreate.add('customerUserErrors', (userErrors: any) => {
          userErrors.add('message'), userErrors.add('field');
          userErrors.add('code');
        });
        customerCreate.add('customer', (user: any) => {
          user.add('id'), user.add('firstName'), user.add('email');
        });
      });
    });

    return from(this.client.send(mutation)).pipe(
      map((a: any) => {
        const errors = a['data']?.customerCreate?.customerUserErrors || a['errors'] || [];
        if (errors.length) {
          throw errors;
        }

        return a['data'];
      })
    ) as Observable<any>;
    // return from(this.client.send(mutation, { input: { lines: [_lineItemForCheckout] } })).pipe(map(({ data }) => data.cartCreate.cart));
  }

  CustomizedCreateCustomerAccessToken(data: CreateAccessTokenData, language?: string) {
    return this.http.post<any>(`${this.BASE_URL}api/customer/login`, data).pipe(
      map((a: any) => {
        const errors = a['data']?.customerAccessTokenCreate?.customerUserErrors || a['errors'] || [];
        if (errors.length) {
          throw errors as ShopifyError[];
        }

        return a['data'].customerAccessTokenCreate.customerAccessToken as CustomerAccessToken;
      })
    ) as Observable<any>;
  }

  SendInvitation(email: string) {
    return this.http.get<{ success: boolean }>(`${this.BASE_URL}api/customer/send-invite?email=${email}`);
  }

  CreateCustomerAccessToken(data: CreateAccessTokenData, language?: string) {
    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerAccessTokenCreate', { args: { input: data } }, (customerAccessTokenCreate: any) => {
        customerAccessTokenCreate.add('customerUserErrors', (userErrors: any) => {
          userErrors.add('message');
          userErrors.add('field');
          userErrors.add('code');
        });
        customerAccessTokenCreate.add('customerAccessToken', (token: any) => {
          token.add('accessToken');
          token.add('expiresAt');
        });
      });
    });

    return from(this.client.send(mutation)).pipe(
      map((a: any) => {
        const errors = a['data']?.customerAccessTokenCreate?.customerUserErrors || a['errors'] || [];
        if (errors.length) {
          throw errors as ShopifyError[];
        }

        return a['data'].customerAccessTokenCreate.customerAccessToken as CustomerAccessToken;
      })
    ) as Observable<any>;
  }

  DeleteCustomerAccessToken(token: string, language?: string) {
    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerAccessTokenDelete', { args: { customerAccessToken: token } }, (customerAccessTokenDelete: any) => {
        customerAccessTokenDelete.add('userErrors', (userErrors: any) => {
          userErrors.add('message');
          userErrors.add('field');
        });
        customerAccessTokenDelete.add('deletedAccessToken');
      });
    });

    return from(this.client.send(mutation)).pipe(
      map((a: any) => {
        const errors = a['data']?.customerAccessTokenDelete?.userErrors || a['errors'] || [];
        if (errors.length) {
          throw errors as ShopifyError[];
        }

        return a['data'].customerAccessTokenDelete.deletedAccessToken as CustomerAccessToken;
      })
    ) as Observable<any>;
  }

  RecoverCustomer(email: string, language?: string) {
    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerRecover', { args: { email } }, (customerCreate: any) => {
        customerCreate.add('customerUserErrors', (userErrors: any) => {
          userErrors.add('message'), userErrors.add('field');
          userErrors.add('code');
        });
        // customerCreate.add('email');
      });
    });

    return from(this.client.send(mutation)).pipe(
      map((a: any) => {
        const errors = a['data']?.customerRecover?.customerUserErrors || a['errors'] || [];
        if (errors.length) {
          throw errors as ShopifyError[];
        }

        return a['data']?.customerRecover;
      })
    ) as Observable<any>;
    // return from(this.client.send(mutation, { input: { lines: [_lineItemForCheckout] } })).pipe(map(({ data }) => data.cartCreate.cart));
  }

  ResetCustomer(resetCustomerData: ResetCustomerData, language?: string) {
    // , [password, resetUrl]
    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerResetByUrl', { args: { ...resetCustomerData } }, (customerReset: any) => {
        customerReset.add('customerUserErrors', (userErrors: any) => {
          userErrors.add('message'), userErrors.add('field');
          userErrors.add('code');
        });
        // customerCreate.add('email');
      });
    });

    return from(this.client.send(mutation)).pipe(
      map((a: any) => {
        const errors = a['data']?.customerResetByUrl?.customerUserErrors || a['errors'] || [];
        if (errors.length) {
          throw errors as ShopifyError[];
        }

        return a['data']?.customerResetByUrl;
      })
    ) as Observable<any>;
    // return from(this.client.send(mutation, { input: { lines: [_lineItemForCheckout] } })).pipe(map(({ data }) => data.cartCreate.cart));
  }

  ActivateCustomer(activateCustomerData: ActivateCustomerData, language?: string) {
    // , [password, activationUrl]
    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerActivateByUrl', { args: { ...activateCustomerData } }, (customerReset: any) => {
        customerReset.add('customerUserErrors', (userErrors: any) => {
          userErrors.add('message'), userErrors.add('field');
          userErrors.add('code');
        });
        // customerCreate.add('email');
      });
    });

    return from(this.client.send(mutation)).pipe(
      map((a: any) => {
        const errors = a['data']?.customerActivateByUrl?.customerUserErrors || a['errors'] || [];
        if (errors.length) {
          throw errors as ShopifyError[];
        }

        return a['data']?.customerActivateByUrl;
      })
    ) as Observable<any>;
    // return from(this.client.send(mutation, { input: { lines: [_lineItemForCheckout] } })).pipe(map(({ data }) => data.cartCreate.cart));
  }

  UpdateCustomer(token: string, customer: Customer, language?: string) {
    let { vat, ...cust } = customer;

    const mutation = this.client.mutation(`@inContext(language: ${language})`, (root: any) => {
      root.add('customerUpdate', { args: { customer: cust, customerAccessToken: token } }, (customerUpdate: any) => {
        customerUpdate.add('customerUserErrors', (userErrors: any) => {
          userErrors.add('message'), userErrors.add('field');
          userErrors.add('code');
        });
        customerUpdate.add('customer', (user: any) => {
          customerProfile(user, this.client);
        });
      });
    });

    return zip(this.updateCustomerVatNumber(token, vat ?? ''), from(this.client.send(mutation))).pipe(
      map(([result, a]) => {
        const data = a as unknown as any;
        const errors = data['data']?.customerUpdate?.customerUserErrors || data['errors'] || [];
        if (errors.length) {
          throw errors;
        }

        return data['data']['customerUpdate']?.customer;
      })
    ) as Observable<any>;
  }

  updateCustomerVatNumber(token: string, vatNumber: string) {
    return this.http.post<{ success: boolean; status: number }>(
      `${this.BASE_URL}api/customer/vat/update?token=${token}&vatNumber=${vatNumber}`,
      null
    );
  }

  checkTaxExemptionStatus(token: string) {
    return this.http.post<{ success: boolean; valid: boolean; status: number }>(
      `${this.BASE_URL}api/customer/vat/check-exemption?token=${token}`,
      null
    );
  }
}

export function customer(queryBase: any, client: any) {
  queryBase.add('id');
  queryBase.add('firstName');
  queryBase.add('lastName');
  queryBase.add('updatedAt');
  queryBase.add('displayName');
  queryBase.add('createdAt');
  queryBase.add('email');
  queryBase.add('phone');
  queryBase.add('acceptsMarketing');
  queryBase.add('defaultAddress', (address: any) => {
    mailingAddress(address, client);
  });
  queryBase.add('metafield', { args: { namespace: 'custom', key: 'vat_number' } }, (metafields: any) => {
    metafields.add('key');
    metafields.add('value');
  });
}

export function customerProfile(queryBase: any, client: any) {
  customer(queryBase, client);
  // TODO add extra fields
}
